import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { brand } from '../../config/brand';

const ContactPage: React.FC = () => {
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch('https://formspree.io/f/xojavkyb', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        const data = await response.json();
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Box>
        {/* Hero Section */}
        <Box
          sx={{
            background: brand.gradients.hero,
            color: 'white',
            py: { xs: 6, md: 10 },
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                component="h1"
                fontWeight="bold"
                sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
              >
                Contact Us
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Success Message */}
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Paper
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper',
            }}
          >
            <Box
              sx={{
                bgcolor: 'success.main',
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Message Sent!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Thank you for reaching out! We've received your message and will get back
              to you as soon as possible. Typically, we respond within 24-48 hours.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: brand.gradients.hero,
          color: 'white',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 2,
              fontSize: '0.875rem',
              mb: 3,
            }}>
              Get in Touch
            </Box>
            <Typography
              variant="h2"
              component="h1"
              fontWeight="bold"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 2,
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Have questions about {brand.productName}? We'd love to hear from you.
              Send us a message and we'll respond as soon as possible.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {/* Contact Info */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 3,
                height: '100%',
                bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper',
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Box sx={{
                  bgcolor: 'primary.main',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <BusinessIcon sx={{ color: 'white' }} />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  {brand.name}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                We're here to help you transform your development process with
                AI-powered tools that prioritize security and compliance.
              </Typography>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <EmailIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="body2" fontWeight="600">
                    Response Time
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  We typically respond within 24-48 hours during business days.
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Paper
              sx={{
                p: { xs: 3, md: 4 },
                bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper',
              }}
            >
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company (Optional)"
                      name="company"
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Your Message"
                      name="message"
                      required
                      multiline
                      rows={5}
                      variant="outlined"
                      placeholder="Tell us how we can help you..."
                    />
                  </Grid>

                  {error && (
                    <Grid item xs={12}>
                      <Alert severity="error">{error}</Alert>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                      endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactPage;
